import { DestroyRef, Directive, ElementRef, NgZone, inject } from '@angular/core'

import _Inputmask from 'inputmask'

// Depending on the bundler the default export is the constructor or a CommonJS wrapper around it.
const Inputmask: typeof _Inputmask =
  (_Inputmask as unknown as { default?: typeof _Inputmask }).default ?? _Inputmask

/**
 * Restricts an input to a `#RRGGBB` hex colour while typing.
 *
 * `@ngneat/input-mask` cannot be combined with `[formField]`: it takes over the element's
 * `NgControl` and adds validators to it, and the `NgControl` that Signal Forms provide is
 * read-only. This applies the same `inputmask` plugin without touching forms; the plugin
 * dispatches `input` events, which `[formField]` reads.
 *
 * `[formField]` also writes the control value back into the element after every keystroke.
 * inputmask patches the element's `value` setter so that any write re-masks the value and moves
 * the caret to the end of the field, which stops typing after the first character. The setter is
 * wrapped so that a write of the value the element already shows is ignored.
 */
@Directive({ selector: 'input[flxHexColorMask]' })
export class FlxHexColorMaskDirective {
  constructor() {
    const element = inject<ElementRef<HTMLInputElement>>(ElementRef).nativeElement
    const mask = inject(NgZone).runOutsideAngular(() =>
      // Without an empty placeholder the value of a partly typed colour reads `#a1____`.
      new Inputmask({ regex: '#[A-Fa-f0-9]{6}', placeholder: '' }).mask(element)
    )
    ignoreUnchangedValueWrites(element)
    // remove() puts the native value property back, replacing the wrapper too.
    inject(DestroyRef).onDestroy(() => mask.remove())
  }
}

const ignoreUnchangedValueWrites = (element: HTMLInputElement): void => {
  const masked = Object.getOwnPropertyDescriptor(element, 'value')
  const get = masked?.get
  const set = masked?.set
  if (!get || !set) {
    return
  }
  Object.defineProperty(element, 'value', {
    configurable: true,
    get,
    set(value: string) {
      if (value !== get.call(this)) {
        set.call(this, value)
      }
    },
  })
}
